import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import NamedEntity from "../interfaces/NamedEntity";

export enum UserRole {
	Admin = "Admin",
	StateAdmin = "StateAdmin",
	DistrictAdmin = "DistrictAdmin",
	SchoolAdmin = "Principal",
	Proctor = "Proctor",
	Writer = "Writer",
	Learner = "SelfServiceWriter",
}

export interface Accessibility {
	fontSize: string | null;
	colorContrast: string | null;
	tintOverlay: string | null;
	lineReader: string | null;
}

export const nullAccessibility: Accessibility = {
	fontSize: null,
	colorContrast: null,
	tintOverlay: null,
	lineReader: null,
};

export interface User {
	userId: number | null;
	username: string | null;
	firstName: string | null;
	lastName: string | null;
	initials: string | null;
	role: UserRole | null;
	isTeachersWriterAccount: boolean | null;
	requiresAccountConfirmation: boolean | null;
	bypassTermsOfService: boolean | null;
	userImageUploadDisabled: boolean | null;
	hasImage: boolean | null;
	accessibility: Accessibility | null;
}

const nullUser: User = {
	userId: null,
	username: null,
	firstName: null,
	lastName: null,
	initials: null,
	role: null,
	isTeachersWriterAccount: null,
	requiresAccountConfirmation: null,
	bypassTermsOfService: null,
	userImageUploadDisabled: null,
	hasImage: null,
	accessibility: null,
};

export interface CourseState {
	id: number;
	name: string;
	gradeId: number;
	isActive: boolean;
	showUserAvatars?: boolean;
	isFavorite?: boolean;
}

export interface UserState extends User {
	isLoggedIn: boolean;
	isCourseInTransition: boolean;
	imageHasChanged: boolean;
	courses: CourseState[] | null;
	course: CourseState | null;
	school: NamedEntity | null;
	district: NamedEntity | null;
}

const initialState: UserState = {
	...nullUser,
	isLoggedIn: false,
	isCourseInTransition: false,
	imageHasChanged: false,
	courses: null,
	course: null,
	school: null,
	district: null,
};

export const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		resetUserState(state) {
			Object.assign(state, initialState);
		},
		setIsLoggedIn(state, action: PayloadAction<boolean>) {
			state.isLoggedIn = action.payload;
		},
		setUser(state, action: PayloadAction<User>) {
			state.userId = action.payload.userId;
			state.username = action.payload.username;
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
			state.initials = action.payload.initials;
			state.userImageUploadDisabled = action.payload.userImageUploadDisabled;
			state.hasImage = action.payload.hasImage;
			state.role = action.payload.role;
			state.isTeachersWriterAccount = action.payload.isTeachersWriterAccount;
			state.requiresAccountConfirmation = action.payload.requiresAccountConfirmation;
			state.bypassTermsOfService = action.payload.bypassTermsOfService;
			state.accessibility = action.payload.accessibility;
		},
		setHasImage(state, action: PayloadAction<boolean>) {
			state.hasImage = action.payload;
			state.imageHasChanged = true;
		},
		setImageHasChanged(state, action: PayloadAction<boolean>) {
			state.imageHasChanged = action.payload;
		},
		setAccessibility(state, action: PayloadAction<Accessibility>) {
			state.accessibility = action.payload;
		},
		clearAccessibility(state) {
			state.accessibility = null;
		},
		setCourses(state, action: PayloadAction<CourseState[]>) {
			state.courses = action.payload;
		},
		setCourse(state, action: PayloadAction<CourseState>) {
			state.isCourseInTransition = false;
			state.course = { ...action.payload };
		},
		transitionCourse(state) {
			state.isCourseInTransition = true;
			state.course = null;
		},
		clearCourse(state) {
			state.isCourseInTransition = false;
			state.course = null;
		},
		setSchool(state, action: PayloadAction<NamedEntity>) {
			state.school = { ...action.payload };
		},
		setDistrict(state, action: PayloadAction<NamedEntity>) {
			state.district = { ...action.payload };
		},
	},
});

export const {
	resetUserState,
	setIsLoggedIn,
	setUser,
	setHasImage,
	setImageHasChanged,
	setAccessibility,
	clearAccessibility,
	setCourses,
	setCourse,
	transitionCourse,
	clearCourse,
	setSchool,
	setDistrict,
} = userSlice.actions;

export default userSlice.reducer;
